import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import ReactECharts from "echarts-for-react";
import {
  DollarSign,
  Receipt,
  TrendingUp,
  Coins,
  PieChart,
  ArrowDownUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const FinancialDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-projects`, {}, { headers: { Authorization: `Bearer ${token}` } });
        const response = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProjects(response.data.projects || []);
      } catch (error) {
        console.error("Error fetching financial data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Calculate financial metrics
  const totalBudget = projects.reduce((acc, p) => acc + (p.budget_total || 0), 0);
  const totalSpent = projects.reduce((acc, p) => acc + (p.budget_spent || 0), 0);
  const budgetUtilization = totalBudget > 0 ? Math.round(totalSpent / totalBudget * 100) : 0;

  const formatCurrency = (val) => {
    if (val >= 1000000000) return `$${(val / 1000000000).toFixed(2)}B`;
    if (val >= 1000000) return `$${(val / 1000000).toFixed(0)}M`;
    return `$${val?.toLocaleString() || 0}`;
  };

  // Group projects by sector for budget vs actual
  const sectorBudgets = projects.reduce((acc, p) => {
    const sector = p.sector || 'Other';
    if (!acc[sector]) acc[sector] = { budget: 0, actual: 0 };
    acc[sector].budget += (p.budget_total || 0);
    acc[sector].actual += (p.budget_spent || 0);
    return acc;
  }, {});

  const budgetVsActualOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' },
      formatter: (params) => {
        return params.map(p => `${p.seriesName}: ${formatCurrency(p.value)}`).join('<br/>');
      }
    },
    legend: {
      data: ['Budget', 'Actual'],
      textStyle: { color: '#94a3b8' },
      top: 0
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
    xAxis: {
      type: 'category',
      data: Object.keys(sectorBudgets).slice(0, 6),
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: '#94a3b8', rotate: 15 }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#1e293b' } },
      axisLabel: { 
        color: '#94a3b8',
        formatter: (val) => formatCurrency(val)
      }
    },
    series: [
      {
        name: 'Budget',
        type: 'bar',
        data: Object.values(sectorBudgets).slice(0, 6).map(d => d.budget),
        itemStyle: { color: '#3b82f6', borderRadius: [4, 4, 0, 0] }
      },
      {
        name: 'Actual',
        type: 'bar',
        data: Object.values(sectorBudgets).slice(0, 6).map(d => d.actual),
        itemStyle: { color: '#c9a227', borderRadius: [4, 4, 0, 0] }
      }
    ]
  };

  // Generate burn rate trend
  const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const burnRateData = months.map((m, i) => ({
    month: m,
    rate: 40 + i * 3 + Math.floor(Math.random() * 5)
  }));

  const burnRateOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: burnRateData.map(d => d.month),
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: '#94a3b8' }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#1e293b' } },
      axisLabel: { color: '#94a3b8', formatter: '{value}%' }
    },
    series: [{
      data: burnRateData.map(d => d.rate),
      type: 'line',
      smooth: true,
      lineStyle: { color: '#ef4444', width: 3 },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(239, 68, 68, 0.3)' },
            { offset: 1, color: 'rgba(239, 68, 68, 0)' }
          ]
        }
      },
      itemStyle: { color: '#ef4444' }
    }]
  };

  // Country breakdown
  const countryBreakdown = projects.reduce((acc, p) => {
    const country = p.country || 'Other';
    acc[country] = (acc[country] || 0) + (p.budget_total || 0);
    return acc;
  }, {});

  const currencyBreakdownOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' },
      formatter: '{b}: ${c}M ({d}%)'
    },
    legend: {
      orient: 'vertical',
      right: '5%',
      top: 'center',
      textStyle: { color: '#94a3b8' }
    },
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['35%', '50%'],
      avoidLabelOverlap: false,
      label: { show: false },
      emphasis: {
        label: { show: true, fontSize: 14, fontWeight: 'bold', color: '#f8fafc' }
      },
      labelLine: { show: false },
      data: Object.entries(countryBreakdown).slice(0, 6).map(([name, value]) => ({ name, value: Math.round(value / 1000000) })),
      itemStyle: {
        borderRadius: 4,
        borderColor: '#0b1c2d',
        borderWidth: 2
      },
      color: ['#c9a227', '#3b82f6', '#22c55e', '#a855f7', '#ef4444', '#06b6d4']
    }]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading financial dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financial-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Financial Management Dashboard
        </h1>
        <p className="text-slate-400 mt-1">Track budget execution, expenses, and financial transparency</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Budget Allocated</p>
                <p className="text-3xl font-bold text-[#c9a227] mt-2">{formatCurrency(totalBudget)}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[#c9a227]/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-[#c9a227]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Expenses Incurred</p>
                <p className="text-3xl font-bold text-red-400 mt-2">{formatCurrency(totalSpent)}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-red-500/10 flex items-center justify-center">
                <Receipt className="w-6 h-6 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Cash Flow Status</p>
                <Badge className="mt-2 status-green text-base capitalize">Positive</Badge>
              </div>
              <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Budget Utilization</p>
                <p className="text-3xl font-bold text-blue-400 mt-2">{budgetUtilization}%</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-blue-500/10 flex items-center justify-center">
                <Coins className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Budget vs Actual */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">Budget vs Actual Expenditure</CardTitle>
        </CardHeader>
        <CardContent>
          <ReactECharts option={budgetVsActualOptions} style={{ height: '350px' }} />
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Expense Burn Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={burnRateOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Currency Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={currencyBreakdownOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>
      </div>

      {/* PFMA Transparency */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">PFMA Transparency Indicators</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'Budget Disclosure', status: 'Compliant', score: 98 },
              { name: 'Expenditure Reporting', status: 'Compliant', score: 95 },
              { name: 'Audit Trail', status: 'Compliant', score: 100 },
              { name: 'Public Accountability', status: 'Compliant', score: 92 }
            ].map((item) => (
              <div key={item.name} className="p-4 bg-slate-800/30 rounded-sm text-center">
                <p className="text-slate-400 text-sm">{item.name}</p>
                <p className="text-2xl font-bold text-green-400 mt-2">{item.score}%</p>
                <Badge className="mt-2 status-green">{item.status}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FinancialDashboard;
